import { useFinance } from '../hooks/useWpApi';
import { SilviaLoader } from '../components/SilviaLoader';
import { RefreshCw, DollarSign, TrendingUp, TrendingDown, AlertTriangle, CreditCard, Wallet, Calendar, ArrowRight } from 'lucide-react';
import '../styles/Finance.css';

export function Finance() {
  const { data, loading, error, refresh, lastRefresh } = useFinance(120_000);

  if (loading && !data) {
    return <SilviaLoader text="Ładowanie finansów..." />;
  }

  if (error && !data) {
    return (
      <div className="stats-error">
        <AlertTriangle size={32} />
        <p>{error}</p>
        <button className="btn-refresh" onClick={refresh}>Spróbuj ponownie</button>
      </div>
    );
  }

  if (!data) return null;

  const { finance, payout_forecast, today, returns_stock, sales_comparison } = data;

  return (
    <div className="stats-page">
      <div className="stats-header">
        <div className="stats-header-top">
          <h1>💰 Finanse</h1>
          <button className="btn-icon-refresh" onClick={refresh} title="Odśwież">
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          </button>
        </div>
        {lastRefresh && (
          <span className="stats-updated">Aktualizacja: {lastRefresh.toLocaleTimeString('pl-PL')}</span>
        )}
      </div>

      {/* ── Revenue & Margin Hero ── */}
      <section className="stats-section">
        <div className="finance-hero">
          <div className="finance-hero-main">
            <span className="finance-hero-label">Przychód dziś</span>
            <span className="finance-hero-amount">{today.orders_amount.toFixed(0)} zł</span>
            <span className="finance-hero-sub">{today.orders_count} zamówień</span>
          </div>
          <div className="finance-hero-divider" />
          <div className="finance-hero-margin">
            <span className="finance-hero-label">Marża</span>
            <span className={`finance-hero-amount ${today.margin >= 0 ? 'positive' : 'negative'}`}>
              {today.margin.toFixed(0)} zł
            </span>
            <span className="finance-hero-sub">{today.margin_pct}% marży</span>
          </div>
        </div>
      </section>

      {/* ── Year over Year ── */}
      <section className="stats-section">
        <h2 className="section-title"><TrendingUp size={18} />Porównanie roczne</h2>
        <div className="yoy-card">
          <div className="yoy-row">
            <div className="yoy-item">
              <span className="yoy-period">📅 Rok temu</span>
              <span className="yoy-value">{sales_comparison.last_year.amount.toFixed(0)} zł</span>
              <span className="yoy-count">{sales_comparison.last_year.count} zam.</span>
            </div>
            <ArrowRight size={20} className="yoy-arrow" />
            <div className="yoy-item highlight">
              <span className="yoy-period">🛍️ Dziś</span>
              <span className="yoy-value">{sales_comparison.today.amount.toFixed(0)} zł</span>
              <span className="yoy-count">{sales_comparison.today.count} zam.</span>
            </div>
          </div>
          <div className={`yoy-change ${sales_comparison.change_pct >= 0 ? 'positive' : 'negative'}`}>
            {sales_comparison.change_pct >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            <span>{sales_comparison.change_pct >= 0 ? '+' : ''}{sales_comparison.change_pct}%</span>
          </div>
        </div>
      </section>

      {/* ── Costs Breakdown ── */}
      <section className="stats-section">
        <h2 className="section-title"><CreditCard size={18} />Koszty</h2>
        <div className="costs-list">
          <CostItem icon="📦" label="Produkty dostawcy" amount={today.supplier_cost} color="gray" />
          {(today.transport_cost ?? 0) > 0 && (
            <CostItem icon="🚚" label="Transport" amount={today.transport_cost ?? 0} color="amber" />
          )}
          {(today.prep_cost ?? 0) > 0 && (
            <CostItem icon="📋" label="Przygotowanie" amount={today.prep_cost ?? 0} color="teal" />
          )}
          <CostItem icon="f" label="Meta/Facebook Ads" amount={today.meta_ads} color="fbblue" />
          <CostItem icon="G" label="Google Ads" amount={today.google_ads} color="green" />
          <div className="costs-total">
            <span>Razem koszty</span>
            <span className="costs-total-amount">{today.total_costs.toFixed(0)} zł</span>
          </div>
        </div>
      </section>

      {/* ── FB Ads Comparison ── */}
      <section className="stats-section">
        <h2 className="section-title"><span className="fb-icon">f</span> Facebook Ads</h2>
        <div className="fb-comparison">
          <div className="fb-item current">
            <span className="fb-label">Dziś</span>
            <span className="fb-amount">{finance.fb_today.toFixed(0)} zł</span>
          </div>
          <div className="fb-item previous">
            <span className="fb-label">Rok temu</span>
            <span className="fb-amount">{finance.fb_last_year.toFixed(0)} zł</span>
          </div>
          {finance.fb_last_year > 0 && (
            <div className={`fb-change ${finance.fb_today <= finance.fb_last_year ? 'positive' : 'negative'}`}>
              {finance.fb_today <= finance.fb_last_year
                ? `📉 ${(((finance.fb_last_year - finance.fb_today) / finance.fb_last_year) * 100).toFixed(0)}% mniej`
                : `📈 ${(((finance.fb_today - finance.fb_last_year) / finance.fb_last_year) * 100).toFixed(0)}% więcej`}
            </div>
          )}
        </div>
      </section>

      {/* ── Cash Flow ── */}
      <section className="stats-section">
        <h2 className="section-title"><Wallet size={18} />Przepływy gotówki</h2>
        <div className="cashflow-grid">
          <CashflowCard
            icon="🏦" title="Przelew (on-hold)"
            count={finance.przelew.count} amount={finance.przelew.amount}
            color="lightblue" />
          <CashflowCard
            icon="📬" title="Pobranie (zamrożone)"
            count={finance.pobranie.count} amount={finance.pobranie.amount}
            color="blue" />
          <CashflowCard
            icon="💵" title="Wypłata dziś"
            count={finance.dzis.count} amount={finance.dzis.amount}
            color="green" />
          <CashflowCard
            icon="📆" title={finance.jutro.label || 'Jutro'}
            count={finance.jutro.count} amount={finance.jutro.amount}
            color="teal" />
        </div>
      </section>

      {/* ── Payout Forecast ── */}
      {payout_forecast && payout_forecast.length > 0 && (
        <section className="stats-section">
          <h2 className="section-title"><Calendar size={18} />Prognoza wypłat</h2>
          <div className="payout-timeline">
            {payout_forecast.map((day, i) => {
              const maxAmount = Math.max(...payout_forecast.map(d => d.amount), 1);
              const barWidth = Math.max((day.amount / maxAmount) * 100, 5);
              return (
                <div key={i} className="payout-day">
                  <div className="payout-day-header">
                    <span className="payout-day-label">{day.label}</span>
                    <span className="payout-day-date">{day.date}</span>
                  </div>
                  <div className="payout-day-bar-bg">
                    <div className="payout-day-bar" style={{ width: `${barWidth}%` }} />
                  </div>
                  <div className="payout-day-footer">
                    <span className="payout-day-amount">{day.amount.toFixed(0)} zł</span>
                    <span className="payout-day-count">{day.count} op.</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Returns Stock ── */}
      {returns_stock && returns_stock.units > 0 && (
        <section className="stats-section">
          <h2 className="section-title"><DollarSign size={18} />Magazyn zwrotów</h2>
          <div className="returns-card">
            <div className="returns-stat">
              <span className="returns-number">{returns_stock.units}</span>
              <span className="returns-label">produktów</span>
            </div>
            <div className="returns-stat">
              <span className="returns-number">{returns_stock.value}</span>
              <span className="returns-label">wartość (zł)</span>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function CostItem({ icon, label, amount, color }: { icon: string; label: string; amount: number; color: string }) {
  return (
    <div className={`cost-item color-${color}`}>
      <span className="cost-icon">{icon}</span>
      <span className="cost-label">{label}</span>
      <span className="cost-amount">{amount.toFixed(0)} zł</span>
    </div>
  );
}

function CashflowCard({ icon, title, count, amount, color }: {
  icon: string; title: string; count: number; amount: number; color: string;
}) {
  return (
    <div className={`cashflow-card color-${color}`}>
      <div className="cashflow-icon">{icon}</div>
      <div className="cashflow-body">
        <div className="cashflow-title">{title}</div>
        <div className="cashflow-amount">{amount.toFixed(0)} zł</div>
        <div className="cashflow-count">{count} zam.</div>
      </div>
    </div>
  );
}
